import {
	type FetchNftExternalAddressesParamsNetworkTypes,
	hoursToMs,
	type NFTExternalAddress,
	queryClient
} from '@capsicum/common';
import { type QueryKey, useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { fetchExternalAddresses } from '../api';

const externalAddressesQueryKey: QueryKey = ['recipients'];

export type UseExternalAddressQueryOptions = Omit<
	UseQueryOptions<Array<NFTExternalAddress>, unknown, Array<NFTExternalAddress> | undefined>,
	'queryFn' | 'queryKey' | 'select'
>;

/**
 * @param recipientId
 * @returns NFTExternalAddress
 */
export const useExternalAddressQuery = <TData = NFTExternalAddress | undefined>(
	recipientId: string,
	networkTypes: Array<FetchNftExternalAddressesParamsNetworkTypes>,
	select?: (data: Array<NFTExternalAddress> | undefined) => TData,
	options?: UseExternalAddressQueryOptions
) =>
	useQuery({
		initialData: () =>
			queryClient
				?.getQueryData<Array<NFTExternalAddress>>(externalAddressesQueryKey)
				?.filter((recipient: NFTExternalAddress) => recipient?.id === recipientId),
		initialDataUpdatedAt: () => (queryClient.getQueryState(externalAddressesQueryKey)?.dataUpdatedAt || 0) * 1000, // convert unix timestamp to JS timestamp in milliseconds
		queryFn: () => fetchExternalAddresses({ 'ids[]': [recipientId], 'networkTypes[]': networkTypes }),
		queryKey: ['recipient', recipientId, networkTypes],
		select,
		staleTime: hoursToMs(1),
		...options,
		enabled: !!recipientId && !!networkTypes && networkTypes.length > 0
	});
